import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Cloud, Database, Wrench, GitBranch } from 'lucide-react';

const categoryConfig = {
    "Programming": { icon: Code2, color: "text-blue-400", bg: "bg-blue-500/10" },
    "Cloud & Infrastructure": { icon: Cloud, color: "text-cyan-400", bg: "bg-cyan-500/10" },
    "Databases & Frameworks": { icon: Database, color: "text-violet-400", bg: "bg-violet-500/10" },
    "Platforms & Tools": { icon: Wrench, color: "text-amber-400", bg: "bg-amber-500/10" },
    "Methodologies": { icon: GitBranch, color: "text-emerald-400", bg: "bg-emerald-500/10" },
};

const Skills = ({ skills }) => {
    return (
        <section id="skills" className="py-24 md:py-32 bg-background relative">
            {/* Background accent */}
            <div className="absolute right-0 bottom-1/4 w-1/4 h-1/3 bg-secondary/3 blur-[120px] pointer-events-none" />

            <div className="max-w-6xl mx-auto px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="text-primary font-mono text-sm tracking-wider uppercase mb-3 block">
                        Technical Skills
                    </span>
                    <h2 className="text-3xl md:text-5xl font-display font-bold text-text tracking-tight">
                        Core Expertise
                    </h2>
                    <p className="text-text-secondary max-w-2xl mx-auto mt-4">
                        A blend of engineering and support, enabling me to solve complex problems end-to-end.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {skills?.map((skill, index) => {
                        const config = categoryConfig[skill.name] || { icon: Code2, color: "text-primary", bg: "bg-primary/10" };
                        const Icon = config.icon;

                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="group p-6 rounded-2xl bg-surface border border-border hover:border-primary/20 hover:shadow-card-hover transition-all duration-500"
                            >
                                {/* Category header */}
                                <div className="flex items-center gap-3 mb-5">
                                    <div className={`p-2 rounded-lg ${config.bg} ${config.color}`}>
                                        <Icon size={20} />
                                    </div>
                                    <h3 className="text-lg font-bold text-text group-hover:text-primary transition-colors">
                                        {skill.name}
                                    </h3>
                                </div>

                                {/* Keywords */}
                                <div className="flex flex-wrap gap-2">
                                    {skill.keywords.map((keyword, idx) => (
                                        <span
                                            key={idx}
                                            className="text-xs font-medium text-text-secondary bg-surface-highlight px-3 py-1.5 rounded-lg border border-border hover:border-primary/20 hover:text-primary transition-colors duration-300"
                                        >
                                            {keyword}
                                        </span>
                                    ))}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Skills;
